// UserTable.tsx
import React from "react";

import { useAppSelector } from "@assets/hooks/redux";
import { Divider, Flex, Section, Table } from "@samskip/frontend-components";
import { useTranslation } from "react-i18next";

const UserTableComponent: React.FC = () => {
  const { t } = useTranslation();
  const userProfile = useAppSelector((state) => state.UserReducer.userProfile);

  const user = userProfile?.User;
  const access = userProfile?.Access;
  const roles = userProfile?.Roles ?? [];

  const userHeaders = [
    { id: "username", name: t("LABEL_USER_NAME"), className: "w-1/5" },
    { id: "name", name: t("LABEL_NAME"), className: "w-1/5" },
    { id: "email", name: t("LABEL_EMAIL"), className: "w-1/5" },
    { id: "created", name: t("LABEL_CREATED_AT"), className: "w-1/5" },
    { id: "lastLogin", name: t("LABEL_LAST_LOGIN"), className: "w-1/5" },
  ];

  const userCells = [
    access?.LoginID,
    user?.Username,
    access?.Email,
    user?.Created ? new Date(user.Created).toLocaleDateString("en-GB") : "",
    access?.LastLogin
      ? `${new Date(access.LastLogin).toLocaleDateString("en-GB")} ${new Date(
          access.LastLogin
        ).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}`
      : "",
  ];

  const systemHeaders = [
    { id: "userID", name: t("LABEL_USER_ID"), className: "w-1/5" },
    { id: "loginID", name: t("LABEL_NAME"), className: "w-1/5" },
    { id: "userType", name: t("LABEL_DESCRIPTION"), className: "w-1/5" },
    { id: "subSys", name: t("LABEL_SUBSYSTEM"), className: "w-1/5" },
    { id: "active", name: t("LABEL_EMPLOYEE"), className: "w-1/5" },
  ];

  const systemCells = access
    ? [access.UserID, access.LoginID, access.UserType, access.SubSys, access.Active]
    : [];

  const roleHeaders = [
    { id: "roleID", name: t("LABEL_ROLE_ID"), className: "w-1/5" },
    { id: "roleName", name: t("LABEL_NAME"), className: "w-1/5" },
    { id: "roleDescription", name: t("LABEL_DESCRIPTION"), className: "w-1/5" },
    { id: "employee", name: t("LABEL_EMPLOYEE"), className: "w-1/5" },
    { id: "customer", name: t("LABEL_CUSTOMER"), className: "w-1/5" },
  ];

  return (
    <Flex gap="large" vertical>
      <Section>
        <Table id="user">
          <Table.Header>
            <Table.HeaderRow>
              {userHeaders.map((header) => (
                <Table.HeaderCell className={header.className} id={header.id} key={header.id}>
                  {header.name}
                </Table.HeaderCell>
              ))}
            </Table.HeaderRow>
          </Table.Header>
          <Table.Body >
            <Table.Row id={String(access?.LoginID)}>
              {userCells.map((cell, index) => (
                <Table.Cell
                  className={userHeaders[index].className}
                  ellipsis="normal"
                  id={userHeaders[index].id}
                  key={userHeaders[index].id}
                >
                  {cell || ""}
                </Table.Cell>
              ))}
            </Table.Row>
          </Table.Body>
        </Table>
      </Section>
      <Divider />
      <Section title={t("LABEL_ACCESS_TO_SYSTEMS")}>
        <Table className="w-full" id="access-systems">
          <Table.Header>
            <Table.HeaderRow>
              {systemHeaders.map((header) => (
                <Table.HeaderCell className={header.className} id={header.id} key={header.id}>
                  {header.name}
                </Table.HeaderCell>
              ))}
            </Table.HeaderRow>
          </Table.Header>
          <Table.Body>
            {systemCells.length > 0 && (
              <Table.Row id={String(access?.UserID)}>
                {systemCells.map((cell, index) => (
                  <Table.Cell
                    className={systemHeaders[index].className}
                    id={systemHeaders[index].id}
                    key={systemHeaders[index].id}
                  >
                    {cell}
                  </Table.Cell>
                ))}
              </Table.Row>
            )}
          </Table.Body>
        </Table>
      </Section>
      <Divider />
      <Section title={t("LABEL_ACCESS_GROUPS")}>
        <Table className="w-full" id="access-groups">
          <Table.Header>
            <Table.HeaderRow>
              {roleHeaders.map((header) => (
                <Table.HeaderCell className={header.className} id={header.id} key={header.id}>
                  {header.name}
                </Table.HeaderCell>
              ))}
            </Table.HeaderRow>
          </Table.Header>
          <Table.Body>
            {roles.map((role) => (
              <Table.Row id={String(role.RoleID)} key={role.RoleID}>
                {[
                  role.RoleID,
                  role.RoleName,
                  role.RoleDescription,
                  role.Employee,
                  role.Customer,
                ].map((cell, index) => (
                  <Table.Cell
                    className={roleHeaders[index].className}
                    id={roleHeaders[index].id}
                    key={roleHeaders[index].id}
                  >
                    {cell}
                  </Table.Cell>
                ))}
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      </Section>
    </Flex>
  );
};

export default UserTableComponent;
